/*
 * xm-product.js – Produktseite USB-Weihnachts-Projektor
 * Galerie, Variantenauswahl, Pakete, Add to Cart, Sticky-Leiste, Liefertermin.
 * Keine externen Abhängigkeiten.
 */
(() => {
  'use strict';
 
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
 
  /* ---------- Hilfsfunktionen ---------- */
 
  function readJSON(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[pp] JSON konnte nicht gelesen werden:', selector, err);
      return null;
    }
  }
 
  function decodeEntities(str) {
    const t = document.createElement('textarea');
    t.innerHTML = str;
    return t.value;
  }
 
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const tpl = decodeEntities(String(format || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, ''));
    const fmt = (amount, decimals, thousands, decimal) => {
      const [int, dec] = (amount / 100).toFixed(decimals).split('.');
      const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intFmt + decimal + dec : intFmt;
    };
    return tpl.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
        default: return fmt(value, 2, '.', ',');
      }
    });
  }
 
  function emitCartUpdate(cart, sourceId) {
    // Horizon und viele OS-2.0-Themes hören auf "cart:update" und aktualisieren Zähler/Drawer.
    const detail = {
      resource: cart,
      sourceId,
      data: { source: 'pp-product', itemCount: cart.item_count }
    };
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
  }
 
  /* ---------- Produktlogik ---------- */
 
  class PPProduct {
    constructor(root) {
      this.root = root;
      this.cfg = readJSON(root, '[data-pp-config]') || {};
      this.variants = readJSON(root, '[data-pp-variants]') || [];
      this.t = this.cfg.strings || {};
      this.form = root.querySelector('[data-pp-form]');
      this.idInput = root.querySelector('[data-pp-variant-id]');
      this.picker = root.querySelector('[data-pp-picker]');
      this.groups = this.picker ? [...this.picker.querySelectorAll('[data-option-index]')] : [];
      this.bundleSet = root.querySelector('[data-pp-bundles]');
      this.tiers = this.bundleSet ? [...this.bundleSet.querySelectorAll('[data-pp-tier]')] : [];
      this.qtyInput = root.querySelector('[data-pp-qty-input]');
      this.atcButtons = [...root.querySelectorAll('[data-pp-atc], [data-pp-sticky-atc]')];
      this.express = root.querySelector('[data-pp-express]');
      this.errorEl = root.querySelector('[data-pp-error]');
      this.live = root.querySelector('[data-pp-live]');
      this.busy = false;
 
      const startId = this.idInput ? Number(this.idInput.value) : null;
      this.current = this.variants.find((v) => v.id === startId) || this.variants[0] || null;
 
      this.initGallery();
      this.initPicker();
      this.initBundles();
      this.initStepper();
      this.initForm();
      this.initSticky();
      this.initToast();
      initDelivery(root);
      this.render();
 
      if (this.current && this.current.media) this.goToMedia(this.current.media, false);
    }
 
    byId(id) {
      return this.variants.find((v) => v.id === Number(id)) || null;
    }
 
    /* ----- Galerie ----- */
 
    initGallery() {
      const g = this.root.querySelector('[data-pp-gallery]');
      this.track = g ? g.querySelector('[data-pp-track]') : null;
      this.slides = [];
      this.thumbs = [];
      this.index = 0;
      if (!this.track) return;
 
      this.slides = [...this.track.querySelectorAll('[data-pp-slide]')];
      this.thumbs = [...g.querySelectorAll('[data-pp-thumb]')];
      this.counter = g.querySelector('[data-pp-counter]');
 
      const prev = g.querySelector('[data-pp-prev]');
      const next = g.querySelector('[data-pp-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));
      this.thumbs.forEach((th, i) => th.addEventListener('click', () => this.goTo(i)));
 
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      });
 
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach((s) => io.observe(s));
      }
    }
 
    goTo(i, smooth = true) {
      const n = this.slides.length;
      if (!n || !this.track) return;
      const idx = ((i % n) + n) % n;
      this.track.scrollTo({
        left: this.slides[idx].offsetLeft,
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto'
      });
      this.setActive(idx);
    }
 
    goToMedia(mediaId, smooth) {
      const i = this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
      if (i > -1 && i !== this.index) this.goTo(i, smooth);
    }
 
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      this.thumbs.forEach((t, k) => {
        const on = k === i;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      this.slides.forEach((s, k) => {
        const video = s.querySelector('video');
        if (video && k !== i) video.pause();
      });
      const th = this.thumbs[i];
      if (th && th.parentElement) {
        const c = th.parentElement;
        c.scrollTo({
          left: th.offsetLeft - c.clientWidth / 2 + th.clientWidth / 2,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
    }
 
    /* ----- Varianten ----- */
 
    initPicker() {
      if (!this.picker) return;
      this.picker.addEventListener('change', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        const group = input.closest('[data-option-index]');
        const idx = group ? Number(group.dataset.optionIndex) : 0;
        let v = this.findVariant(this.selectedOptions());
        if (!v || !v.available) {
          const alt = this.variants.find((x) => x.options[idx] === input.value && x.available);
          if (alt) {
            v = alt;
            this.checkOptions(alt.options);
          }
        }
        if (v) this.setVariant(v, true);
      });
    }
 
    selectedOptions() {
      return this.groups.map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
 
    findVariant(opts) {
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    }
 
    checkOptions(opts) {
      this.groups.forEach((fs, i) => {
        fs.querySelectorAll('input[type="radio"]').forEach((inp) => {
          inp.checked = inp.value === opts[i];
        });
      });
    }
 
    setVariant(v, fromUser) {
      this.current = v;
      if (this.idInput) this.idInput.value = v.id;
      this.unitSelects().forEach((s) => {
        if (!s.dataset.touched) s.value = String(v.id);
      });
      if (fromUser && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }
      this.render();
      if (fromUser && v.media) this.goToMedia(v.media, true);
      this.root.dispatchEvent(new CustomEvent('pp:variant-change', { bubbles: true, detail: { variant: v } }));
    }
 
    updateOptions() {
      if (!this.groups.length) return;
      const sel = this.selectedOptions();
      const singleOption = this.groups.length === 1;
      this.groups.forEach((fs, i) => {
        const label = fs.querySelector('[data-pp-option-label]');
        if (label) label.textContent = sel[i] || '';
        fs.querySelectorAll('input[type="radio"]').forEach((inp) => {
          const test = sel.slice();
          test[i] = inp.value;
          const match = this.findVariant(test);
          const ok = !!(match && match.available);
          const chip = inp.closest('.pp-chip');
          if (chip) {
            chip.classList.toggle('is-unavailable', !ok);
            chip.classList.toggle('is-selected', inp.checked);
          }
          // Bei nur einer Option: nicht verfügbare Werte sperren.
          // Bei mehreren Optionen: nur durchstreichen, damit man andere Kombinationen erreicht.
          if (singleOption) inp.disabled = !ok && !inp.checked;
        });
      });
    }
 
    /* ----- Pakete & Menge ----- */
 
    unitSelects(scope) {
      const s = scope || this.bundleSet;
      return s ? [...s.querySelectorAll('[data-pp-unit]')] : [];
    }
 
    selectedTier() {
      return this.tiers.find((t) => {
        const r = t.querySelector('input[type="radio"]');
        return r && r.checked;
      }) || this.tiers[0];
    }
 
    tierItems(tier) {
      const qty = Number(tier.dataset.ppTier) || 1;
      const sels = this.unitSelects(tier);
      if (sels.length) return sels.map((s) => this.byId(s.value) || this.current);
      return Array.from({ length: qty }, () => this.current);
    }
 
    initBundles() {
      if (!this.bundleSet) return;
      this.bundleSet.addEventListener('change', (e) => {
        const el = e.target;
        if (el.matches('[data-pp-unit]')) el.dataset.touched = '1';
        this.render();
      });
    }
 
    initStepper() {
      if (!this.qtyInput) return;
      const clamp = () => {
        let n = parseInt(this.qtyInput.value, 10);
        const max = Number(this.qtyInput.max) || 99;
        if (!Number.isFinite(n) || n < 1) n = 1;
        if (n > max) n = max;
        this.qtyInput.value = n;
        this.render();
      };
      const step = (d) => {
        this.qtyInput.value = (parseInt(this.qtyInput.value, 10) || 1) + d;
        clamp();
      };
      const minus = this.root.querySelector('[data-pp-qty-minus]');
      const plus = this.root.querySelector('[data-pp-qty-plus]');
      if (minus) minus.addEventListener('click', () => step(-1));
      if (plus) plus.addEventListener('click', () => step(1));
      this.qtyInput.addEventListener('change', clamp);
    }
 
    selectionItems() {
      if (!this.current) return [];
      if (this.tiers.length) return this.tierItems(this.selectedTier());
      const q = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1;
      return Array.from({ length: q }, () => this.current);
    }
 
    getLineItems() {
      const map = new Map();
      this.selectionItems().forEach((v) => map.set(v.id, (map.get(v.id) || 0) + 1));
      return [...map].map(([id, quantity]) => ({ id, quantity }));
    }
 
    /* ----- Darstellung aktualisieren ----- */
 
    render() {
      const v = this.current;
      if (!v) return;
      const fm = (c) => formatMoney(c, this.cfg.moneyFormat);
      const q = (sel) => this.root.querySelector(sel);
 
      this.updateOptions();
 
      // Preis
      const onSale = v.compare > v.price;
      const now = q('[data-pp-price-now]');
      if (now) now.textContent = fm(v.price);
      const wasWrap = q('[data-pp-price-was-wrap]');
      if (wasWrap) wasWrap.hidden = !onSale;
      const was = q('[data-pp-price-was]');
      if (was && onSale) was.textContent = fm(v.compare);
      const save = q('[data-pp-price-save]');
      if (save) {
        save.hidden = !onSale;
        if (onSale) save.textContent = `-${Math.floor(((v.compare - v.price) * 100) / v.compare)} %`;
      }
      const sku = q('[data-pp-sku]');
      if (sku) sku.textContent = v.sku || '';
 
      // Pakete
      this.tiers.forEach((tier) => {
        const radio = tier.querySelector('input[type="radio"]');
        const on = !!(radio && radio.checked);
        tier.classList.toggle('is-selected', on);
        const units = tier.querySelector('[data-pp-units]');
        if (units) units.hidden = !on;
        const items = this.tierItems(tier);
        const total = items.reduce((s, x) => s + x.price, 0);
        const ref = items.reduce((s, x) => s + (x.compare > x.price ? x.compare : x.price), 0);
        const priceEl = tier.querySelector('[data-pp-tier-price]');
        if (priceEl) priceEl.textContent = fm(total);
        const wasEl = tier.querySelector('[data-pp-tier-was]');
        if (wasEl) {
          wasEl.hidden = !(ref > total);
          wasEl.textContent = fm(ref);
        }
      });
 
      // Buttons, Express-Checkout, Sticky
      const items = this.selectionItems();
      const available = items.length > 0 && items.every((x) => x.available);
      const mixed = new Set(items.map((x) => x.id)).size > 1;
      const total = items.reduce((s, x) => s + x.price, 0);
 
      this.atcButtons.forEach((b) => {
        if (b.classList.contains('is-loading')) return;
        b.disabled = !available;
        const label = b.querySelector('[data-pp-atc-label]');
        if (label && !b.dataset.flash) label.textContent = available ? this.t.addToCart : this.t.soldOut;
      });
 
      // Express-Kauf nimmt nur eine Variante mit – bei gemischten Farben ausblenden.
      if (this.express) this.express.hidden = mixed || !available;
 
      const stickyPrice = q('[data-pp-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = fm(total);
      const stickyVariant = q('[data-pp-sticky-variant]');
      if (stickyVariant) {
        let text = '';
        if (items.length > 1) text = `${items.length} ${this.t.unitPlural}`;
        else if (this.variants.length > 1) text = v.title;
        stickyVariant.textContent = text;
        stickyVariant.hidden = !text;
      }
      const stickyImg = q('[data-pp-sticky-img] img');
      if (stickyImg && v.img) {
        stickyImg.removeAttribute('srcset');
        stickyImg.src = v.img;
      }
    }
 
    /* ----- Warenkorb ----- */
 
    initForm() {
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.add();
        });
      }
      const stickyBtn = this.root.querySelector('[data-pp-sticky-atc]');
      if (stickyBtn) stickyBtn.addEventListener('click', () => this.add());
    }
 
    async add() {
      if (this.busy) return;
      const items = this.getLineItems();
      if (!items.length) return;
      this.busy = true;
      this.setLoading(true);
      this.showError('');
      try {
        const res = await fetch(this.cfg.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || this.t.error);
        }
        const cartRes = await fetch(`${this.cfg.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();
        this.afterAdd(cart, items);
      } catch (err) {
        this.showError(err && err.message ? err.message : this.t.error);
      } finally {
        this.busy = false;
        this.setLoading(false);
      }
    }
 
    setLoading(on) {
      this.atcButtons.forEach((b) => {
        b.classList.toggle('is-loading', on);
        b.setAttribute('aria-busy', on ? 'true' : 'false');
        if (on) b.disabled = true;
      });
      if (!on) this.render();
    }
 
    flash() {
      this.atcButtons.forEach((b) => {
        b.dataset.flash = '1';
        const label = b.querySelector('[data-pp-atc-label]');
        if (label) label.textContent = this.t.added;
      });
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        this.atcButtons.forEach((b) => delete b.dataset.flash);
        this.render();
      }, 2200);
    }
 
    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }
 
    announce(msg) {
      if (!this.live) return;
      this.live.textContent = '';
      window.setTimeout(() => { this.live.textContent = msg; }, 50);
    }
 
    afterAdd(cart, items) {
      const count = items.reduce((s, i) => s + i.quantity, 0);
      this.announce(`${count} × ${this.cfg.productTitle} ${this.t.addedLive}`);
      this.flash();
      emitCartUpdate(cart, this.root.id);
 
      const mode = this.cfg.afterAdd;
      if (mode === 'cart') {
        window.location.href = this.cfg.cartUrl;
        return;
      }
      if (mode === 'drawer') {
        const drawer = document.querySelector('cart-drawer-component, cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          try {
            drawer.open();
            return;
          } catch (err) {
            console.warn('[pp] Drawer konnte nicht geöffnet werden, zeige Bestätigung.', err);
          }
        }
      }
      this.showToast(cart);
    }
 
    /* ----- Bestätigung ----- */
 
    initToast() {
      this.toast = this.root.querySelector('[data-pp-toast]');
      if (!this.toast) return;
      const close = this.toast.querySelector('[data-pp-toast-close]');
      if (close) close.addEventListener('click', () => this.hideToast());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hideToast();
      });
      const hold = () => clearTimeout(this.toastTimer);
      this.toast.addEventListener('mouseenter', hold);
      this.toast.addEventListener('focusin', hold);
    }
 
    showToast(cart) {
      if (!this.toast) return;
      const text = this.toast.querySelector('[data-pp-toast-text]');
      if (text) {
        text.textContent = `${cart.item_count} ${this.t.item} im Warenkorb, ${this.t.subtotal} ${formatMoney(cart.total_price, this.cfg.moneyFormat)}`;
      }
      this.toast.inert = false;
      this.toast.removeAttribute('aria-hidden');
      this.toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.hideToast(), 7000);
    }
 
    hideToast() {
      if (!this.toast || !this.toast.classList.contains('is-visible')) return;
      this.toast.classList.remove('is-visible');
      this.toast.inert = true;
      this.toast.setAttribute('aria-hidden', 'true');
    }
 
    /* ----- Sticky-Leiste ----- */
 
    initSticky() {
      this.sticky = this.root.querySelector('[data-pp-sticky]');
      const anchor = this.root.querySelector('[data-pp-atc]');
      if (!this.sticky || !anchor || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.inert = !show;
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.documentElement.classList.toggle('pp-sticky-on', show);
      });
      io.observe(anchor);
    }
  }
 
  /* ---------- Liefertermin ---------- */
 
  function initDelivery(root) {
    root.querySelectorAll('[data-pp-delivery]').forEach((el) => {
      const d = el.dataset;
      const n = (k) => Math.max(0, parseInt(d[k], 10) || 0);
      const pMin = n('procMin');
      const pMax = Math.max(pMin, n('procMax'));
      const sMin = n('shipMin');
      const sMax = Math.max(sMin, n('shipMax'));
      let f;
      try {
        f = new Intl.DateTimeFormat(document.documentElement.lang || 'de-DE', { day: 'numeric', month: 'short' });
      } catch (err) {
        f = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' });
      }
      const addWorkdays = (days) => {
        const dt = new Date();
        let left = days;
        while (left > 0) {
          dt.setDate(dt.getDate() + 1);
          const wd = dt.getDay();
          if (wd !== 0 && wd !== 6) left -= 1;
        }
        return dt;
      };
      const range = (a, b) => (a === b ? f.format(addWorkdays(a)) : `${f.format(addWorkdays(a))} – ${f.format(addWorkdays(b))}`);
      const ship = el.querySelector('[data-pp-date-ship]');
      if (ship) ship.textContent = range(pMin, pMax);
      const arrive = el.querySelector('[data-pp-date-arrive]');
      if (arrive) arrive.textContent = range(pMin + sMin, pMax + sMax);
    });
  }
 
  /* ---------- Videos (So funktioniert's) ---------- */
 
  function initVideos(scope) {
    scope.querySelectorAll('[data-pp-autoplay] video').forEach((video) => {
      if (video.dataset.ppInit) return;
      video.dataset.ppInit = '1';
      if (reduceMotion) {
        video.pause();
        video.removeAttribute('autoplay');
        return;
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        }, { threshold: 0.25 }).observe(video);
      }
    });
  }
 
  /* ---------- Start ---------- */
 
  function init(scope) {
    const s = scope || document;
    s.querySelectorAll('[data-pp-product]').forEach((root) => {
      if (root.ppInstance) return;
      root.ppInstance = new PPProduct(root);
    });
    initVideos(s);
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
 